/**
 * useWlTenantDetails.ts
 * ----------------------------------------------------------------------------
 * Hooks de leitura para o Modal de Detalhes do Tenant (painel White Label).
 * Cada hook chama uma RPC exclusiva `wl_*` (SECURITY DEFINER) — frontend NUNCA
 * acessa tabelas diretamente, conforme contrato WL.
 *
 * RPCs consumidas:
 *   - wl_get_tenant_overview            (header + KPIs 30d)
 *   - wl_get_tenant_kpi_timeseries      (série diária default 7 dias)
 *   - wl_list_tenant_inboxes            (inboxes do tenant)
 *   - wl_list_tenant_members_detail     (membros mascarados)
 *   - wl_get_tenant_billing_summary     (resumo financeiro)
 *   - wl_list_tenant_billing_history    (histórico de faturas)
 *
 * RELEASE: 2026-05-29 v1.0.0
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Tipagens públicas
// ---------------------------------------------------------------------------
export interface WlTenantOverview {
  tenant_id: string;
  name: string;
  slug: string;
  status: string;
  plan: string;
  plan_name: string | null;
  created_at: string;
  trial_ends_at: string | null;
  suspension_reason: string | null;
  subscription_status: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  owner_user_id: string | null;
  owner_name: string | null;
  owner_email_masked: string | null;
  owner_phone_masked: string | null;
  members_count: number;
  agents_count: number;
  inboxes_count: number;
  active_inboxes_count: number;
  addons_count: number;
  conversations_30d: number;
  messages_in_30d: number;
  messages_out_30d: number;
  deals_30d: number;
  tasks_30d: number;
  max_agents: number | null;
  max_channels: number | null;
  // v1.1.0 — limites efetivos (somam add-ons) + uso de mensagens do mês
  effective_max_agents: number | null;
  effective_max_inboxes: number | null;
  effective_max_channels: number | null;
  effective_max_messages: number | null;
  addon_agents: number;
  addon_inboxes: number;
  addon_channels: number;
  messages_month_current: number;
}

export interface WlTenantTimeseriesPoint {
  day: string;
  messages_in: number;
  messages_out: number;
  conversations: number;
  deals_created: number;
  tasks_created: number;
}

export interface WlTenantInbox {
  inbox_id: string;
  name: string;
  channel_type: string;
  provider_name: string | null;
  status: string;
  is_default: boolean;
  conversations_30d: number;
  last_message_at: string | null;
  created_at: string;
}

export interface WlTenantMember {
  member_id: string;
  user_id: string;
  full_name: string | null;
  email_masked: string | null;
  phone_masked: string | null;
  is_owner: boolean;
  is_active: boolean;
  joined_at: string | null;
  last_seen_at: string | null;
  roles: Array<{ role: string }>;
}

export interface WlTenantBillingSummary {
  subscription_status: string | null;
  plan_id: string | null;
  plan_name: string | null;
  plan_price_monthly: number | null;
  plan_price_yearly: number | null;
  billing_period: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
  trial_ends_at: string | null;
  is_in_trial: boolean;
  trial_days_remaining: number;
  canceled_at: string | null;
  setup_fee_paid: boolean;
  active_addons_count: number;
  addons_total_monthly: number;
  mrr: number;
  last_invoice_amount: number | null;
  last_invoice_status: string | null;
  last_invoice_date: string | null;
  // v2.0.0 — KPIs derivados de financeiro_cobrancas (sistema novo)
  open_invoices_count: number;
  open_invoices_total: number;
  overdue_invoices_count: number;
  overdue_invoices_total: number;
  next_due_date: string | null;
}

export interface WlTenantInvoice {
  id: string;
  issued_at: string;
  period_start: string | null;
  period_end: string | null;
  amount_paid: number;
  currency: string | null;
  status: string | null;
  plan_name: string | null;
  billing_period: string | null;
  invoice_url: string | null;
  invoice_pdf: string | null;
  // v1.1.2 — Novas colunas retornadas pela RPC wl_list_tenant_billing_history
  due_date: string | null;
  payment_date: string | null;
  numero_fatura: string | null;
  payment_method_name: string | null;
  bank_account_name: string | null;
  frequencia: string | null;
  total_count: number;
}

// v1.1.0 — Add-on contratado (linha compacta para a Visão Geral)
export interface WlTenantAddonItem {
  id: string;
  addon_id: string;
  addon_name: string;
  addon_slug: string;
  addon_type: string;
  quantity: number;
  status: string;
  price_monthly: number | null;
  // [v1.1.0] snapshot V1 (vindo de wl_list_tenant_addons v1.1.0)
  unit_price: number | null;
  recurrence_interval: string | null;
  billing_integration: string | null;
  gateway_slug: string | null;
  setup_fee_paid: boolean | null;
  activated_at: string | null;
  cancelled_at: string | null;
  current_period_start: string | null;
  current_period_end: string | null;
}

// ---------------------------------------------------------------------------
// Helpers internos
// ---------------------------------------------------------------------------
const STALE = 60_000; // 60s — modal é leitura, evita re-fetch agressivo

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/** Visão geral do tenant (1 linha agregada). */
export function useWlTenantOverview(tenantId: string | null) {
  return useQuery({
    queryKey: ["wl_tenant_overview", tenantId],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantOverview | null> => {
      const { data, error } = await supabase.rpc("wl_get_tenant_overview", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      const row = (data ?? [])[0];
      return (row as WlTenantOverview) ?? null;
    },
  });
}

/** Série temporal diária (default 7 dias, máx 90). */
export function useWlTenantTimeseries(tenantId: string | null, days = 7) {
  return useQuery({
    queryKey: ["wl_tenant_timeseries", tenantId, days],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantTimeseriesPoint[]> => {
      const { data, error } = await supabase.rpc("wl_get_tenant_kpi_timeseries", {
        p_tenant_id: tenantId,
        p_days: days,
        p_timezone: "America/Sao_Paulo",
      });
      if (error) throw error;
      return (data ?? []) as WlTenantTimeseriesPoint[];
    },
  });
}

/** Inboxes do tenant. */
export function useWlTenantInboxes(tenantId: string | null) {
  return useQuery({
    queryKey: ["wl_tenant_inboxes", tenantId],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantInbox[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenant_inboxes", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return (data ?? []) as WlTenantInbox[];
    },
  });
}

/** Membros do tenant (exclui root_admin via RPC). */
export function useWlTenantMembers(tenantId: string | null) {
  return useQuery({
    queryKey: ["wl_tenant_members", tenantId],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantMember[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenant_members_detail", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      return (data ?? []) as WlTenantMember[];
    },
  });
}

/** Resumo financeiro (plano, trial, MRR, última fatura). */
export function useWlTenantBillingSummary(tenantId: string | null) {
  return useQuery({
    queryKey: ["wl_tenant_billing_summary", tenantId],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantBillingSummary | null> => {
      const { data, error } = await supabase.rpc("wl_get_tenant_billing_summary", {
        p_tenant_id: tenantId,
      });
      if (error) throw error;
      const row = (data ?? [])[0];
      return (row as WlTenantBillingSummary) ?? null;
    },
  });
}

/** Histórico de faturas paginado. */
export function useWlTenantBillingHistory(
  tenantId: string | null,
  opts: { limit?: number; offset?: number } = {},
) {
  const { limit = 50, offset = 0 } = opts;
  return useQuery({
    queryKey: ["wl_tenant_billing_history", tenantId, limit, offset],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantInvoice[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenant_billing_history", {
        p_tenant_id: tenantId,
        p_limit: limit,
        p_offset: offset,
      });
      if (error) throw error;
      return (data ?? []) as WlTenantInvoice[];
    },
  });
}

/** Lista de add-ons ativos do tenant (para a aba Visão Geral). */
export function useWlTenantAddons(
  tenantId: string | null,
  status: string = "active",
) {
  return useQuery({
    queryKey: ["wl_tenant_addons", tenantId, status],
    enabled: !!tenantId,
    staleTime: STALE,
    queryFn: async (): Promise<WlTenantAddonItem[]> => {
      const { data, error } = await supabase.rpc("wl_list_tenant_addons", {
        p_tenant_id: tenantId!,
        p_status: status,
        p_limit: 200,
        p_offset: 0,
      });
      if (error) throw error;
      return ((data ?? []) as any[]).map((r) => ({
        id: r.id,
        addon_id: r.addon_id,
        addon_name: r.addon_name,
        addon_slug: r.addon_slug,
        addon_type: r.addon_type,
        quantity: r.quantity,
        status: r.status,
        price_monthly: r.price_monthly ?? null,
        unit_price: r.unit_price ?? null,
        recurrence_interval: r.recurrence_interval ?? null,
        billing_integration: r.billing_integration ?? null,
        gateway_slug: r.gateway_slug ?? null,
        setup_fee_paid: r.setup_fee_paid ?? null,
        activated_at: r.activated_at ?? null,
        cancelled_at: r.cancelled_at ?? null,
        current_period_start: r.current_period_start ?? null,
        current_period_end: r.current_period_end ?? null,
      }));
    },
  });
}

// ---------------------------------------------------------------------------
// [v1.1.0] Mutations de add-ons V1 (painel WL)
// Encapsulam as RPCs wl_tenant_addon_add / wl_tenant_addon_set_quantity /
// wl_tenant_addon_remove. Cada uma dispara internamente
// tenant_addon_apply_change → sync com financeiro master (uaizy) e
// reprovisionamento de limites.
// ---------------------------------------------------------------------------
import { useMutation, useQueryClient } from "@tanstack/react-query";

export function useWlTenantAddonMutations(tenantId: string | null) {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["wl_tenant_addons", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_billing_summary", tenantId] });
    qc.invalidateQueries({ queryKey: ["wl_tenant_overview", tenantId] });
  };

  const add = useMutation({
    mutationFn: async (input: { addon_id: string; quantity?: number }) => {
      if (!tenantId) throw new Error("tenant_id ausente");
      const { data, error } = await supabase.rpc("wl_tenant_addon_add", {
        p_tenant_id: tenantId,
        p_addon_id: input.addon_id,
        p_quantity: input.quantity ?? 1,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const setQuantity = useMutation({
    mutationFn: async (input: { addon_id: string; quantity: number }) => {
      if (!tenantId) throw new Error("tenant_id ausente");
      const { data, error } = await supabase.rpc("wl_tenant_addon_set_quantity", {
        p_tenant_id: tenantId,
        p_addon_id: input.addon_id,
        p_quantity: input.quantity,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const remove = useMutation({
    mutationFn: async (input: { addon_id: string }) => {
      if (!tenantId) throw new Error("tenant_id ausente");
      const { data, error } = await supabase.rpc("wl_tenant_addon_remove", {
        p_tenant_id: tenantId,
        p_addon_id: input.addon_id,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  return { add, setQuantity, remove };
}
